import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { paymentsApi, getAccessToken } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Landmark, Plus, Trash2, ShieldCheck, ExternalLink, Download } from "lucide-react";

let stripePromise = null;
const getStripe = (pk) => {
  if (!stripePromise && pk) stripePromise = loadStripe(pk);
  return stripePromise;
};

export default function CommandWallet() {
  const [config, setConfig] = useState(null);
  const [methods, setMethods] = useState([]);
  const [txns, setTxns] = useState([]);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m, t] = await Promise.all([
        paymentsApi.config(),
        paymentsApi.listMethods(),
        paymentsApi.transactions(),
      ]);
      setConfig(c);
      setMethods(m);
      setTxns(t);
    } catch (e) { console.error("wallet load failed", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Detach this payment method from Umbrella?")) return;
    try { await paymentsApi.removeMethod(id); toast.success("Payment method removed"); load(); }
    catch (e) { console.error("remove PM failed", e); toast.error("Failed to remove"); }
  };

  const exportCsv = async () => {
    try {
      // Use fetch with credentials so the httpOnly cookie is sent (Bearer header
      // fallback added for the in-tab sessionStorage path).
      const headers = {};
      const tok = getAccessToken();
      if (tok) headers.Authorization = `Bearer ${tok}`;
      const r = await fetch(paymentsApi.exportCsvUrl(), { credentials: "include", headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `umbrella-charges-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Charges CSV downloaded");
    } catch (e) {
      console.error("csv export failed", e);
      toast.error("CSV export failed");
    }
  };

  return (
    <section className="uc-card p-6 uc-fade" data-testid="command-wallet">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="uc-chip mb-2"><ShieldCheck size={11}/> COMMAND WALLET · STRIPE</div>
          <h2 className="font-serif-display text-2xl">Payment Methods on File</h2>
          <p className="text-white/50 text-xs mt-1">
            Executor &amp; Controller can propose charges — every dollar still needs your explicit Commander tap.
            Cards &amp; US bank accounts stored as tokenized Stripe references, never raw PAN.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="wallet-mode-chip"
            className={`font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${config?.mode === "live" ? "border-emerald-500/50 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}
          >
            {config?.mode === "live" ? "LIVE" : "TEST MODE"}
          </span>
          <Button
            data-testid="wallet-add-btn"
            onClick={() => setAdding(true)}
            disabled={!config?.enabled}
            className="uc-btn-amber rounded-none h-9"
          >
            <Plus size={12} className="mr-1"/> Add Method
          </Button>
        </div>
      </div>

      {config?.mode !== "live" && (
        <GoLivePanel onActivated={load} />
      )}

      {!config?.enabled && (
        <div className="p-3 border border-amber-500/30 bg-amber-500/5 text-[11px] text-white/70 mb-4">
          Stripe is not fully configured on this Command Center yet.
          {config?.onboarding_url && (
            <a href={config.onboarding_url} target="_blank" rel="noreferrer" className="ml-1 text-amber-400 underline inline-flex items-center gap-1">
              Claim your Stripe account <ExternalLink size={10}/>
            </a>
          )}
        </div>
      )}

      {loading && <div className="text-white/40 text-sm">Loading wallet…</div>}
      {!loading && methods.length === 0 && (
        <div className="text-white/40 text-[11px] italic py-4 border border-dashed border-white/10 text-center">
          No payment methods yet. Add a card or US bank so agents can request charges you approve.
        </div>
      )}

      <div className="space-y-2">
        {methods.map((m) => (
          <div key={m.id} className="flex items-center justify-between p-3 border border-white/10 hover:border-white/20 transition-colors" data-testid={`pm-${m.id}`}>
            <div className="flex items-center gap-3">
              {m.type === "card" ? <CreditCard size={18} className="text-teal-400"/> : <Landmark size={18} className="text-teal-400"/>}
              <div>
                <div className="font-mono-uc text-sm uppercase tracking-wider">{m.label}</div>
                <div className="font-mono-uc text-[10px] text-white/40">{m.type === "card" ? "Card" : "US Bank"} · ••{m.last4}</div>
              </div>
            </div>
            <button data-testid={`pm-remove-${m.id}`} onClick={() => remove(m.id)} className="text-white/40 hover:text-red-400 transition-colors">
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
      </div>

      {txns.length > 0 && (
        <div className="pt-4 mt-4 border-t border-white/10">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50">Recent Charges</div>
            <button
              data-testid="wallet-export-csv"
              onClick={exportCsv}
              className="font-mono-uc text-[10px] uppercase tracking-widest text-teal-400 hover:text-teal-200 transition-colors flex items-center gap-1 px-2 py-1 border border-teal-500/30 hover:bg-teal-500/10"
            >
              <Download size={11}/> Export CSV
            </button>
          </div>
          <div className="space-y-1">
            {txns.slice(0, 5).map((t) => (
              <div key={t.id} className="flex items-center justify-between font-mono-uc text-[11px]" data-testid={`txn-${t.id}`}>
                <span className="text-white/70">${Number(t.amount).toFixed(2)}</span>
                <span className={t.payment_status === "paid" ? "text-emerald-400" : "text-red-400"}>{t.payment_status}</span>
                <span className="text-white/40">{new Date(t.created_at).toLocaleString(undefined,{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {adding && config?.enabled && config?.publishable_key && (
        <AddMethodModal
          pk={config.publishable_key}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      )}
    </section>
  );
}

function GoLivePanel({ onActivated }) {
  const [open, setOpen] = useState(false);
  const [pk, setPk] = useState("");
  const [sk, setSk] = useState("");
  const [busy, setBusy] = useState(false);

  const activate = async () => {
    if (!window.confirm("Switch the Command Wallet to LIVE mode? All future charges will hit REAL cards.")) return;
    setBusy(true);
    try {
      const r = await paymentsApi.activateLive(pk.trim(), sk.trim());
      toast.success(`Wallet is LIVE — Stripe account ${r.account_id}`);
      setSk("");
      setOpen(false);
      onActivated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed — nothing was changed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-3 border border-emerald-500/25 bg-emerald-500/5 mb-4" data-testid="wallet-golive-panel">
      {!open ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-white/70">
            Running on managed <span className="text-amber-400">test keys</span>. Bring your own Stripe live keys to accept real cards.
          </div>
          <button
            data-testid="wallet-golive-btn"
            onClick={() => setOpen(true)}
            className="font-mono-uc text-[10px] uppercase tracking-widest px-3 py-1.5 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 transition-colors"
          >
            Go Live
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-emerald-400">Activate Live Mode · keys are verified with Stripe before anything changes</div>
          <input
            data-testid="wallet-golive-pk"
            value={pk}
            onChange={(e) => setPk(e.target.value)}
            placeholder="pk_live_…  (Stripe Dashboard → Developers → API keys)"
            className="w-full h-9 bg-black border border-white/10 focus:border-emerald-500/40 outline-none px-2 font-mono-uc text-xs text-white/80"
          />
          <input
            data-testid="wallet-golive-sk"
            type="password"
            value={sk}
            onChange={(e) => setSk(e.target.value)}
            placeholder="sk_live_…  or rk_live_… (restricted key, recommended)"
            className="w-full h-9 bg-black border border-white/10 focus:border-emerald-500/40 outline-none px-2 font-mono-uc text-xs text-white/80"
          />
          <div className="flex gap-2">
            <button
              data-testid="wallet-golive-activate"
              onClick={activate}
              disabled={busy || !pk.startsWith("pk_live_") || sk.length < 40}
              className="font-mono-uc text-[10px] uppercase tracking-widest px-3 py-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40 transition-colors"
            >
              {busy ? "Verifying with Stripe…" : "Verify & Activate Live"}
            </button>
            <button onClick={() => setOpen(false)} className="font-mono-uc text-[10px] uppercase tracking-widest px-3 py-2 border border-white/10 text-white/50 hover:bg-white/5">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddMethodModal({ pk, onClose, onSaved }) {
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await paymentsApi.setupIntent();
        if (!cancelled) setClientSecret(r.client_secret);
      } catch (e) {
        console.error("setup-intent failed", e);
        toast.error("Could not start Stripe setup");
        onClose();
      }
    })();
    return () => { cancelled = true; };
  }, [onClose]);

  // Rendered through a portal to <body>: the wallet card's fade animation leaves a
  // persistent transform that would otherwise trap this fixed overlay inside the card's
  // stacking context (making it appear BEHIND later cards/tabs). z-[100] keeps it above
  // the sticky header (z-30) and every Radix layer (z-50).
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="wallet-add-modal">
      <div className="bg-[#0A0A0A] border border-white/15 max-w-md w-full p-5 max-h-[90vh] overflow-y-auto uc-scroll" onClick={(e) => e.stopPropagation()}>
        <div className="uc-chip mb-3"><ShieldCheck size={11}/> STRIPE ELEMENTS · SECURE</div>
        <h3 className="font-serif-display text-xl mb-3">Add Payment Method</h3>
        {!clientSecret && <div className="text-white/40 text-sm">Preparing secure form…</div>}
        {clientSecret && (
          <Elements stripe={getStripe(pk)} options={{ clientSecret, appearance: { theme: "night" } }}>
            <SetupForm onCancel={onClose} onSaved={onSaved}/>
          </Elements>
        )}
      </div>
    </div>,
    document.body
  );
}

function SetupForm({ onCancel, onSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (error) { setBusy(false); toast.error(error.message || "Setup failed"); return; }
    try {
      await paymentsApi.saveMethod(setupIntent.payment_method);
      toast.success("Payment method added");
      onSaved();
    } catch { toast.error("Saved with Stripe but failed to register in Umbrella"); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="wallet-setup-form">
      <PaymentElement/>
      <div className="flex gap-2">
        <Button type="button" onClick={onCancel} variant="ghost" className="flex-1 rounded-none h-10 border border-white/10 text-white/70">Cancel</Button>
        <Button type="submit" disabled={!stripe || busy} className="flex-1 rounded-none h-10 uc-btn-amber" data-testid="wallet-setup-submit">
          {busy ? "Saving…" : "Save Method"}
        </Button>
      </div>
    </form>
  );
}
